import { useEffect, useMemo } from 'react';

import FlightsSearchControlsGC, {
  flightsSearchControlsStringMap,
} from '@skyscanner-internal/global-components/flights-search-controls';
import { LOGGING_COMPONENT_NAME } from '@skyscanner-internal/global-components/flights-search-controls/common/constants/tracking';
import {
  fetchCalendarPrices,
  fetchExplorePrices,
  fetchMonthPrices,
  fetchSuggestions,
} from '@skyscanner-internal/global-components/flights-search-controls/data';
import { usePlatform } from '@web-platform/shared-banana-redux/src/helpers/usePlatform';
import { LOGGING } from '@web-platform/shared-universal-constants/src/logging';
import { TRANSLATION_KEYS } from '@web-platform/shared-universal-constants/src/translationKeysOfGlobalComponent';
import {
  getTrustedFunnel,
  getUserIdentifiers,
  getUserSearch,
} from '@web-platform/shared-universal-redux/src/state';
import { translateStrings } from '@web-platform/shared-universal-translations';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import { getCldr, getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { useSelector } from 'react-redux';
import { logError, logWarn, logOperationalEvent } from 'saddlebag-logger';

import { getFeatureDecisions } from './helpers/getFeatureDecisions';
import getInitialSearchState from './helpers/getInitialSearchState';
import { usePackagesParallelSearch, useParallelSearch } from './helpers/useParallelSearch';
import useSearch from './helpers/useSearch';

export type FlightsSearchControlsProps = {
  supportParallelSearch?: boolean;
};

const FlightsSearchControls = ({ supportParallelSearch = false }: FlightsSearchControlsProps) => {
  const { currentScreenName: screenName, viewGuid } = useSelector(getTrustedFunnel);
  const [isParallelSearchOptionEnabled, defaultParallelSearchValue, parallelSearchTracking] =
    useParallelSearch(supportParallelSearch);
  const [
    isPackagesParallelSearchOptionEnabled,
    defaultPackagesParallelSearchValue,
    packagesParallelSearchTracking,
  ] = usePackagesParallelSearch(supportParallelSearch);
  const handleOnSearch = useSearch(
    parallelSearchTracking,
    packagesParallelSearchTracking ?? undefined,
  );

  useEffect(() => {
    if (isParallelSearchOptionEnabled) {
      parallelSearchTracking?.trackImpression(undefined, {
        isParallelSearchOptionEnabled,
        isPackagesParallelSearchOptionEnabled,
      });
    }
  }, [
    isParallelSearchOptionEnabled,
    isPackagesParallelSearchOptionEnabled,
    parallelSearchTracking,
  ]);

  useEffect(() => {
    if (isPackagesParallelSearchOptionEnabled) {
      packagesParallelSearchTracking?.trackImpression(undefined, {
        isParallelSearchOptionEnabled,
        isPackagesParallelSearchOptionEnabled,
      });
    }
  }, [
    isParallelSearchOptionEnabled,
    isPackagesParallelSearchOptionEnabled,
    packagesParallelSearchTracking,
  ]);
  const userSearch = useSelector(getUserSearch);
  const platform = usePlatform();
  const utid = useSelector(getUserIdentifiers)?.utid || '';
  const culture = getCulture();
  const isProduction = process.env.NODE_ENV === 'production';
  const cldr = getCldr();
  const allStrings = useMemo(() => translateStrings(TRANSLATION_KEYS.flightsSearchControls), []);
  const initialSearchState = getInitialSearchState(
    userSearch,
    defaultParallelSearchValue,
    defaultPackagesParallelSearchValue,
  );
  const featureDecisions = getFeatureDecisions(
    isParallelSearchOptionEnabled,
    isPackagesParallelSearchOptionEnabled,
  );

  const trackingProps = {
    platform,
    screenName,
    screenViewEventGuid: viewGuid,
  };

  return (
    <ErrorBoundary componentName={LOGGING_COMPONENT_NAME} fallback={null}>
      <FlightsSearchControlsGC
        onSearch={handleOnSearch}
        cldr={cldr}
        culture={culture}
        strings={flightsSearchControlsStringMap(allStrings)}
        fetchCalendarPrices={fetchCalendarPrices}
        fetchExplorePrices={fetchExplorePrices}
        fetchMonthPrices={fetchMonthPrices}
        fetchSuggestions={fetchSuggestions}
        initialSearchState={initialSearchState}
        logger={{
          logError,
          logWarn,
          logOperationalEvent,
        }}
        featureDecisions={featureDecisions}
        trackingProps={trackingProps}
        utid={utid}
        xSkyscannerClient={LOGGING.PAGE_NAME}
        isProductionEnv={isProduction}
      />
    </ErrorBoundary>
  );
};

export default FlightsSearchControls;
